import type { ICommentAttachment, ICommentLink, ICommentMetadata } from '@atlas/types'
import { apiClient } from './api.client'

/** A comment as returned by comment-service (`DTOOf<IComment>`, dates as ISO strings). */
export interface CommentDTO {
	id: string
	entityType: string
	entityId: string
	/** Null for a comment from an external source, which has no ATLAS author. */
	authorId: string | null
	content: string
	isInternal: boolean
	/** Open-shape metadata; an external comment carries `IExternalCommentMetadata`. */
	metadata?: ICommentMetadata | null
	createdAt: string
	updatedAt: string
}

/** What a user posts: text, reference images and links, any of which may be empty but not all three. */
export interface CommentInput {
	content: string
	attachments: ICommentAttachment[]
	links: ICommentLink[]
}

const unwrap = <T>(body: { data: T }): T => body.data

/**
 * comment-service client. Comments are a generic primitive keyed by
 * `(entityType, entityId)` — for a work order that's `('work_order', id)`.
 * The endpoint is admin-only (ADR-0012); the dev token is admin, so it works in
 * dev/mock. On a non-admin JWT these 403 — callers surface that gracefully.
 */
export const commentApi = {
	listForWorkOrder: (workOrderId: string) =>
		apiClient
			.get<{ data: CommentDTO[] }>('/comments', { params: { entityType: 'work_order', entityId: workOrderId } })
			.then((r) => unwrap(r.data)),
	create: (workOrderId: string, { content, attachments, links }: CommentInput) =>
		apiClient
			.post<{ data: CommentDTO }>('/comments', {
				entityType: 'work_order',
				entityId: workOrderId,
				content,
				metadata: { attachments, links },
			})
			.then((r) => unwrap(r.data)),
	update: (id: string, content: string) =>
		apiClient.put<{ data: CommentDTO }>(`/comments/${id}`, { content }).then((r) => unwrap(r.data)),
	remove: (id: string) => apiClient.delete<void>(`/comments/${id}`).then((r) => r.data),
}
